<script lang="ts">
  import { auth } from "$lib/stores/auth.svelte";
  import { accounts } from "$lib/stores/accounts.svelte";
  import { categories } from "$lib/stores/categories.svelte";
  import { meStore, type Persona } from "$lib/stores/me.svelte";
  import PersonaPicker from "$lib/components/PersonaPicker.svelte";
  import { api } from "$lib/api/client";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { todayISO } from "$lib/utils/format";
  import { navigate } from "$lib/router.svelte";

  let { onDone } = $props<{ onDone: () => void }>();

  const TOTAL = 4;
  let step = $state(1);
  let firstName = $state(auth.user?.name?.split(" ")[0] ?? "");
  let saving = $state(false);

  let persona = $state<Persona>("personal");

  // Step 3 — primeira transação
  let txAmount = $state("");
  let txDescription = $state("");
  let txKind = $state<"income" | "expense">("expense");

  // Step 4 — telegram
  let botToken = $state("");
  let chatId = $state("");
  let testingTg = $state(false);
  let tgTestResult = $state<{ ok: boolean; error?: string } | null>(null);

  const steps = [
    { label: "Seu perfil", icon: "🎯" },
    { label: "Primeira transação", icon: "📝" },
    { label: "Configurar Telegram", icon: "🤖" },
    { label: "Pronto!", icon: "🚀" },
  ];

  async function savePersona() {
    saving = true;
    try {
      await meStore.setPersona(persona);
      step = 2;
    } catch (e: any) {
      toasts.error("Erro", e.message);
    } finally {
      saving = false;
    }
  }

  async function saveTransaction() {
    if (!txAmount || !txDescription) { toasts.warn("Preencha valor e descrição"); return; }
    saving = true;
    try {
      await categories.ensureSeeded();
      const acc = await accounts.ensureDefault();
      const cat = categories.byKind(txKind)[0];
      if (!cat) throw new Error("Sem categoria");
      await api("/transactions", {
        method: "POST",
        body: JSON.stringify({
          account_id: acc.id, category_id: cat.id, kind: txKind,
          amount: txAmount, date: todayISO(),
          description: txDescription, status: "effected", currency: "BRL",
        }),
      });
      toasts.success("Primeira transação registrada!");
      step = 3;
    } catch (e: any) {
      toasts.error("Erro ao salvar", e.message);
    } finally { saving = false; }
  }

  async function saveTelegram() {
    saving = true;
    try {
      const ws = auth.workspaces[0];
      if (!ws) return;
      const body: any = {};
      if (chatId.trim()) body.telegram_chat_id = chatId.trim();
      if (botToken.trim()) body.telegram_bot_token = botToken.trim();
      if (Object.keys(body).length > 0) {
        await api(`/workspaces/${ws.id}/settings`, { method: "PATCH", body: JSON.stringify(body) });
        toasts.success("Telegram configurado!");
      }
      step = 4;
    } catch (e: any) {
      toasts.error("Não foi possível configurar", e.message);
    } finally { saving = false; }
  }

  async function testTelegram() {
    testingTg = true;
    tgTestResult = null;
    try {
      tgTestResult = await api("/telegram/test", { method: "POST" });
    } catch (e: any) {
      tgTestResult = { ok: false, error: e?.body?.detail || e?.message || "Falha" };
    } finally { testingTg = false; }
  }

  function finish() {
    localStorage.setItem("takite.onboarded", "1");
    onDone();
  }

  function skip() { finish(); }
</script>

<div class="modal-backdrop">
  <div class="modal-panel max-w-xl">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold">
        {step < 4 ? `Bem-vindo${firstName ? `, ${firstName}` : ""}!` : "Tudo pronto! 🎉"}
      </h2>
      <span class="text-sm text-muted">Passo {Math.min(step, 3)} de 3</span>
    </div>

    <!-- Barra de progresso -->
    <div class="h-1.5 rounded-full mb-5 overflow-hidden" style="background: var(--border)">
      <div class="h-full bg-brand transition-all duration-500"
           style="width: {step >= 4 ? 100 : ((step - 1) / 3) * 100}%"></div>
    </div>

    <!-- Step 1: Perfil -->
    {#if step === 1}
      <p class="text-sm mb-4 text-muted">
        Em 3 passos você está pronto para controlar suas finanças. Vamos começar?
      </p>
      <ul class="space-y-2 mb-5">
        {#each steps.slice(0,3) as s, i}
          <li class="flex items-center gap-2 text-sm {i === 0 ? 'font-semibold' : 'text-muted'}">
            <span>{s.icon}</span> {s.label}
          </li>
        {/each}
      </ul>
      <div class="mb-5">
        <p class="text-sm font-medium mb-2">Como você vai usar o TáKite?</p>
        <PersonaPicker bind:value={persona} />
      </div>
      <div class="flex justify-between">
        <button class="btn-ghost text-sm" onclick={skip}>Pular tour</button>
        <button class="btn-primary" onclick={savePersona} disabled={saving}>
          {saving ? "Salvando…" : "Continuar →"}
        </button>
      </div>

    <!-- Step 2: Primeira transação -->
    {:else if step === 2}
      <p class="text-sm mb-3 text-muted">
        Registre algo simples — uma compra, um recebimento. Pode apagar depois.
      </p>
      <div class="space-y-3">
        <div class="flex gap-2">
          <button type="button"
                  class="flex-1 py-2 rounded-lg border text-sm font-medium transition-colors
                  {txKind === 'expense' ? 'bg-brand text-white border-brand' : 'border-[var(--border)] hover:border-brand'}"
                  onclick={() => (txKind = 'expense')}>💸 Despesa</button>
          <button type="button"
                  class="flex-1 py-2 rounded-lg border text-sm font-medium transition-colors
                  {txKind === 'income' ? 'bg-emerald-500 text-white border-emerald-500' : 'border-[var(--border)] hover:border-emerald-400'}"
                  onclick={() => (txKind = 'income')}>💰 Receita</button>
        </div>
        <div>
          <label class="label" for="ob-desc">Descrição</label>
          <input id="ob-desc" class="input" bind:value={txDescription}
                 placeholder={txKind === "expense" ? "Almoço no restaurante" : "Salário de maio"} />
        </div>
        <div>
          <label class="label" for="ob-amt">Valor (R$)</label>
          <input id="ob-amt" class="input" inputmode="decimal" bind:value={txAmount} placeholder="0,00" />
        </div>
      </div>
      <div class="flex justify-between mt-5">
        <button class="btn-ghost" onclick={() => (step = 1)}>← Voltar</button>
        <button class="btn-primary" onclick={saveTransaction} disabled={saving}>
          {saving ? "Salvando…" : "Continuar →"}
        </button>
      </div>

    <!-- Step 3: Telegram -->
    {:else if step === 3}
      <p class="text-sm mb-4 text-muted">
        Conecte o Telegram para receber <strong>lembretes diários</strong> e lançar gastos por mensagem de voz ou texto.
      </p>
      <div class="rounded-lg p-3 mb-4 text-sm space-y-1" style="background: var(--surface-alt)">
        <p class="font-medium">Como configurar:</p>
        <ol class="list-decimal list-inside space-y-1 text-muted">
          <li>Abra o <a href="https://t.me/BotFather" target="_blank" class="text-brand underline">@BotFather</a> no Telegram</li>
          <li>Envie <code class="bg-[var(--border)] px-1 rounded">/newbot</code> e siga as instruções</li>
          <li>Cole o token gerado aqui abaixo</li>
          <li>Abra seu novo bot e envie qualquer mensagem</li>
          <li>Cole seu Chat ID (número do seu perfil)</li>
        </ol>
      </div>
      <div class="space-y-3">
        <div>
          <label class="label" for="ob-token">Token do Bot</label>
          <input id="ob-token" class="input font-mono text-xs" bind:value={botToken}
                 placeholder="123456789:AAFabc..." />
        </div>
        <div>
          <label class="label" for="ob-chatid">Seu Chat ID</label>
          <input id="ob-chatid" class="input" bind:value={chatId} placeholder="123456789" inputmode="numeric" />
        </div>
        {#if botToken && chatId}
          <button class="btn-secondary w-full text-sm" onclick={testTelegram} disabled={testingTg}>
            {testingTg ? "Enviando mensagem de teste…" : "🔔 Testar conexão"}
          </button>
          {#if tgTestResult}
            <div class="text-sm rounded p-2 {tgTestResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}">
              {tgTestResult.ok ? "✅ Conectado! Você recebeu uma mensagem no Telegram." : `❌ ${tgTestResult.error}`}
            </div>
          {/if}
        {/if}
      </div>
      <div class="flex justify-between mt-5">
        <button class="btn-ghost" onclick={() => (step = 2)}>← Voltar</button>
        <div class="flex gap-2">
          <button class="btn-ghost" onclick={() => (step = 4)}>Pular</button>
          <button class="btn-primary" onclick={saveTelegram} disabled={saving}>
            {saving ? "Salvando…" : "Continuar →"}
          </button>
        </div>
      </div>

    <!-- Step 4: Concluído -->
    {:else}
      <div class="text-center py-4">
        <div class="text-6xl mb-4">🎉</div>
        <p class="text-lg font-semibold mb-2">Configuração concluída!</p>
        <p class="text-sm text-muted mb-6">
          Seu TáKite está pronto. Explore o painel, registre seus gastos e acompanhe suas metas.
        </p>
        <div class="grid grid-cols-2 gap-3 mb-6 text-left">
          <button class="card text-sm hover:opacity-80 transition-opacity cursor-pointer"
                  onclick={() => { finish(); navigate("/transactions"); }}>
            <div class="text-xl mb-1">📝</div>
            <div class="font-medium">Lançar transação</div>
            <div class="text-xs text-muted">Registrar receita ou despesa</div>
          </button>
          <button class="card text-sm hover:opacity-80 transition-opacity cursor-pointer"
                  onclick={() => { finish(); navigate("/insights"); }}>
            <div class="text-xl mb-1">📊</div>
            <div class="font-medium">Ver insights</div>
            <div class="text-xs text-muted">Gráficos e análises</div>
          </button>
          <button class="card text-sm hover:opacity-80 transition-opacity cursor-pointer"
                  onclick={() => { finish(); navigate("/recorrencias"); }}>
            <div class="text-xl mb-1">🔄</div>
            <div class="font-medium">Recorrências</div>
            <div class="text-xs text-muted">Assinaturas e parcelas</div>
          </button>
          <button class="card text-sm hover:opacity-80 transition-opacity cursor-pointer"
                  onclick={() => { finish(); navigate("/settings"); }}>
            <div class="text-xl mb-1">⚙️</div>
            <div class="font-medium">Configurações</div>
            <div class="text-xs text-muted">Perfil e notificações</div>
          </button>
        </div>
        <button class="btn-primary w-full" onclick={finish}>Ir para o painel →</button>
      </div>
    {/if}
  </div>
</div>
